import React from "react";
import { Box, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";

/**
 * Reusable "click a name to open the profile" primitives.
 *
 * Two goals:
 *   1. One canonical route per profile type — staff → /team-history/{id},
 *      student → /student-history/{id}. Callers pass the raw id, never a
 *      pre-built URL, so every consumer stays consistent.
 *   2. Play nicely inside clickable parents (calendar event cards, table
 *      rows, drawers) — `stopPropagation` on the profile link stops the
 *      outer card from *also* opening its drawer, but the click still
 *      routes normally, and Ctrl/Cmd/middle-click preserve native "open
 *      in new tab" behaviour because we use react-router's <Link>.
 *
 * Both components render as plain text (no bright hyperlink blue) with a
 * hover underline + colour bump, so they blend into premium cards but read
 * as clickable on hover / focus.
 *
 * Fallback: when the id is missing/undefined, render the name as plain text
 * (never route to `/team-history/undefined`). A development-mode console
 * warning surfaces the bad payload without breaking the page.
 */

type BaseProps = {
  id?: number | string | null;
  name?: string | null;
  /** Optional query the profile page may honour (e.g. `?return_url=…`). */
  returnUrl?: string;
  /** Overrides the tooltip. Defaults to "View {name}'s profile". */
  tooltip?: string;
  /** Presentational sx passed to the inner <Box>. */
  sx?: any;
  className?: string;
  /** Render as inline vs. block. Default inline. */
  block?: boolean;
  children?: React.ReactNode;
  /**
   * Explicit destination override. When supplied, replaces the default
   * `${basePath}/${id}` construction so callers can route the same
   * StudentProfileLink to a portal-scoped profile (e.g. Staff Portal's
   * `/staff-portal/students/:id`) without forking the component. `id` is
   * still required for the missing-id fallback (renders plain text).
   */
  to?: string;
};

type ProfileLinkProps = BaseProps & { basePath: string; kind: "staff" | "student" | "parent" };

const InternalProfileLink: React.FC<ProfileLinkProps> = ({
  id,
  name,
  returnUrl,
  tooltip,
  sx,
  className,
  block,
  children,
  basePath,
  kind,
  to,
}) => {
  const label = (children as any) ?? name ?? "";
  const missing = id === undefined || id === null || id === "" || String(id) === "0";

  if (missing) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(`[ProfileLink] Missing ${kind} id for name="${name}" — rendered as plain text.`);
    }
    return (
      <Box component={block ? "div" : "span"} className={className} sx={sx}>
        {label}
      </Box>
    );
  }

  const defaultTo = `${basePath}/${encodeURIComponent(String(id))}${
    returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : ""
  }`;
  const finalTo = to
    ? `${to}${returnUrl ? `?return_url=${encodeURIComponent(returnUrl)}` : ""}`
    : defaultTo;

  const linkBox = (
    <Box
      component={Link as any}
      to={finalTo}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      className={className}
      aria-label={tooltip || (name ? `View ${name}'s profile` : `View ${kind} profile`)}
      sx={{
        display: block ? "block" : "inline",
        color: "inherit",
        textDecoration: "none",
        fontWeight: 600,
        cursor: "pointer",
        borderRadius: "4px",
        transition: "color .12s ease, background-color .12s ease",
        "&:hover": {
          color: "primary.main",
          textDecoration: "underline",
          textUnderlineOffset: 2,
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
        ...(sx || {}),
      }}
    >
      {label}
    </Box>
  );

  const title = tooltip || (name ? `View ${name}'s profile` : `View ${kind} profile`);
  return (
    <Tooltip title={title} arrow enterDelay={400}>
      {linkBox}
    </Tooltip>
  );
};

export const StaffProfileLink: React.FC<BaseProps> = (props) => (
  <InternalProfileLink {...props} basePath="/team-history" kind="staff" />
);

export const StudentProfileLink: React.FC<BaseProps> = (props) => (
  <InternalProfileLink {...props} basePath="/student-history" kind="student" />
);

/**
 * Parent / guardian profile link — routes to /parents/view/:id, matching
 * the destination used inside the Invoice View parent card. Added so the
 * invoice list and any other surface displaying a guardian name never
 * routes to the Staff profile (which used to be the accidental fallback
 * — Galaxy Dong appearing as a staff record was that exact bug).
 */
export const ParentProfileLink: React.FC<BaseProps> = (props) => (
  <InternalProfileLink {...props} basePath="/parents/view" kind="parent" />
);

export default StaffProfileLink;
